import { catchError, filter, map, switchMap, take, mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  DefaultEmailCompleteAction,
  DefaultEmailFailAction,
  DefaultReceivingPersonsLoadAction,
  DefaultReceivingPersonsLoadCompleteAction,
  DefaultReceivingPersonsLoadFailAction,
  LOAD,
  LOAD_COMPLETE,
  LOAD_DEFAULT_CONTACT,
  LOAD_DEFAULT_CONTACT_COMPLETE,
  LOAD_DEFAULT_EMAIL,
  ReceivingPersonsLoadAction,
  ReceivingPersonsLoadCompleteAction,
  ReceivingPersonsLoadFailAction,
  ReceivingPersonsSetAction,
  RemoveSelectedReceivingPerson,
} from '@ordersnproducts/modules/shared/modules/receiving-person/redux/actions/receiving-person.actions';
import { IEffect } from '../../../dynamic-effects/effect.interface';
import { ILegalEntity } from '@cemex-core/cmx-api/models/legal-entities/legal-entity';
import { IContactResponse } from '@cemex-core/cmx-api/models/contact/contact-response.interface';
import { of } from 'rxjs';
import { CONTACT_TYPE, ContactService } from '@cemex-core/cmx-api';
import {
  JOBSITE_SET,
  JobsiteSetAction,
} from '../../../../../order-builder/modules/location/actions/jobsite-actions/jobsite.actions';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { getJobsiteState, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { getReceivingPersonState } from '@ordersnproducts/modules/shared/modules/receiving-person/redux/selectors/index';
import { EntityType } from '@ordersnproducts/modules/shared/models/entity-type.model';
import { ContactTypeCodeEnum } from '@ordersnproducts/modules/order-builder/modules/location/models/contact-type-code.enum';
import { Features } from '@ordersnproducts/features';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { UnicEffectsAvailableEnum } from '@ordersnproducts/modules/shared/modules/dynamic-effects/deletable-effect-sources.constant';
import { EffectsRegistrationService } from '@ordersnproducts/modules/shared/modules/dynamic-effects/effects-registration.service';
import { IDummyEmailResponse } from '@cemex-core/cmx-api/models/contact/contact-dummy-email';
import { DEFAULT_COUNTRY_CODE } from '@ordersnproducts/modules/shared/constants/contacts-dummy-emails';

@Injectable()
export class ReceivingPersonsLoadEffects implements IEffect {
  LoadReceivingPersonsAfterJobsiteSet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JOBSITE_SET),
      filter(() => this.isEffectAvailable()),
      map((payload: JobsiteSetAction) => {
        return (
          new RemoveSelectedReceivingPerson(),
          new ReceivingPersonsLoadAction({
            contactType: [CONTACT_TYPE.JOBSITE],
            jobsiteId: payload.payload.shipmentLocationId,
          })
        );
      }),
    ),
  );

  LoadReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD),
      filter(() => this.isEffectAvailable()),
      mergeMap((payload: ReceivingPersonsLoadAction) => {
        return this.store.select(getLegalEntity).pipe(
          take(1),
          map((customer: ILegalEntity) => customer),
          mergeMap((customer: ILegalEntity) => {
            return this.contactService.getJobsiteContactsV4({
              legalEntityId: `${customer.legalEntityId}.1`,
              shipmentLocationId: `${payload.payload.jobsiteId}.2`,
              contactTypeCode: [ContactTypeCodeEnum.JOBSITE, ContactTypeCodeEnum.JOBSITE_REQUEST].join(','),
            });
          }),
        );
      }),
      map((response: IContactResponse) => {
        return new ReceivingPersonsLoadCompleteAction(response.contacts);
      }),
      catchError((error) => {
        console.error(error);
        return of(new ReceivingPersonsLoadFailAction(error));
      }),
    ),
  );

  LoadDefaultReceivingPersonsAfterJobsiteSet$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_COMPLETE),
      filter(
        () =>
          !!this.featureToggleService.isFeatureEnabled(Features.LoadDefaultReceivingPerson) && this.isEffectAvailable(),
      ),
      switchMap(() => {
        return this.store.select(getJobsiteState).pipe(
          take(1),
          filter((state) => !!state.selected && !!state.selected.shipmentLocationCode),
          map((state) => {
            return new DefaultReceivingPersonsLoadAction({
              jobsiteCode: state.selected.shipmentLocationCode,
            });
          }),
        );
      }),
    ),
  );

  LoadDefaultReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_DEFAULT_CONTACT),
      filter(() => this.isEffectAvailable()),
      switchMap((action: DefaultReceivingPersonsLoadAction) => {
        return this.store.select(getReceivingPersonState).pipe(
          take(1),
          filter((state) => !state.selected || !state.selected.name),
          map(() => action),
        );
      }),
      switchMap((action: DefaultReceivingPersonsLoadAction) => {
        return this.contactService
          .getContactsByJobsite({
            jobsiteCode: action.payload.jobsiteCode,
            entityType: EntityType.ORDER_REQUEST,
          })
          .pipe(
            map((response: any) => {
              return new DefaultReceivingPersonsLoadCompleteAction(response.contacts);
            }),
            catchError((error) => of(new DefaultReceivingPersonsLoadFailAction(error))),
          );
      }),
    ),
  );

  SetDefaultReceivingPersons$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_DEFAULT_CONTACT_COMPLETE),
      filter((action: DefaultReceivingPersonsLoadCompleteAction) => !!action.payload[0] && this.isEffectAvailable()),
      switchMap((action: DefaultReceivingPersonsLoadCompleteAction) => {
        const apiContact = action.payload[0];
        return this.store.select(getReceivingPersonState).pipe(
          take(1),
          filter(
            (state) =>
              !!state.receivingPersons.find((contact) => {
                return Number(apiContact.contactId) === Number(contact.contactId);
              }),
          ),
          map(() => action),
        );
      }),
      map((action: DefaultReceivingPersonsLoadCompleteAction) => {
        const apiContact = action.payload[0];
        const defaultContact = {
          contactId: apiContact.contactId,
          contactCode: apiContact.contactCode,
          name: apiContact.contactName,
          phone: apiContact.contactPhone,
        };
        return new ReceivingPersonsSetAction(defaultContact, true);
      }),
    ),
  );

  DefaultEmailAction$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_DEFAULT_EMAIL),
      switchMap((_payload) =>
        this.contactService.getDefaultEmail(DEFAULT_COUNTRY_CODE).pipe(
          map((response: IDummyEmailResponse) => {
            return new DefaultEmailCompleteAction(response.settings[0].parameterValue);
          }),
          catchError((error) => of(new DefaultEmailFailAction())),
        ),
      ),
    ),
  );

  /**
   * Identifies if the effect is available to be executed
   * @returns {boolean}
   */
  private isEffectAvailable(): boolean {
    return this.effectRegister.isEffectAvailable(UnicEffectsAvailableEnum.ReceivingPersonsLoadEffects);
  }

  constructor(
    private actions$: Actions,
    private contactService: ContactService,
    private store: Store<OrderBuilderState>,
    private featureToggleService: FeatureToggleService,
    private effectRegister: EffectsRegistrationService,
  ) {}
}
